/**
 * Authentication service for managing user sessions
 */


import { useState, useEffect } from 'react';
import { isTokenExpired } from '../utils/jwt';
import { LicenseService } from './LicenseService';
import { getBaseUrl } from '../utils/api-client';

// User type definition matching the server response
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  avatar?: string;
}

// Helper to get full name
export const getFullName = (user: User): string => {
  return `${user.firstName} ${user.lastName}`.trim();
};

// Auth state interface
interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  loading: boolean;
}

// Token storage keys
export const TOKEN_KEY = 'smartcity_auth_token';
export const USER_KEY = 'smartcity_user';

/**
 * Authentication service class for managing user authentication
 */
export class AuthService {
  /**
   * Get the authentication token from storage
   * @returns Auth token or null if not found
   */
  static getToken(): string | null {
    if (typeof window === 'undefined') return null;

    // Try localStorage first
    const localToken = localStorage.getItem(TOKEN_KEY);
    if (localToken && !isTokenExpired(localToken)) {
      return localToken;
    }

    // Try sessionStorage next
    const sessionToken = sessionStorage.getItem(TOKEN_KEY);
    if (sessionToken && !isTokenExpired(sessionToken)) {
      return sessionToken;
    }

    // Try to read from cookie as last resort
    const getCookie = (name: string): string | null => {
      const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return match ? match[2] : null;
    };

    const cookieToken = getCookie(TOKEN_KEY) || getCookie('auth-token');
    if (cookieToken && !isTokenExpired(cookieToken)) {
      // If found in cookie but not in storage, sync to storage for consistency
      this.setToken(cookieToken);
      return cookieToken;
    }

    // If we reach here, no valid token was found
    return null;
  }

  /**
   * Set the authentication token in storage
   * @param token Token to store
   */
  static setToken(token: string): void {
    if (typeof window === 'undefined') return;

    // Store token in all storage locations for redundancy
    localStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(TOKEN_KEY, token);

    // Set as cookie for API requests (httpOnly cookies can only be set by server)
    document.cookie = `${TOKEN_KEY}=${token}; path=/; max-age=86400`;

    // Also set as auth-token for backward compatibility
    document.cookie = `auth-token=${token}; path=/; max-age=86400`;
  }

  /**
   * Get the current user data from storage
   * @returns User object or null if not found
   */
  static getCurrentUser(): User | null {
    if (typeof window === 'undefined') return null;

    const userJson = localStorage.getItem(USER_KEY);
    if (!userJson) return null;

    try {
      return JSON.parse(userJson);
    } catch (e) {
      console.error('Error parsing user JSON:', e);
      return null;
    }
  }

  /**
   * Set user data in storage
   * @param user User data to store
   */
  static setCurrentUser(user: User): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  /**
   * Login user with email and password
   * @param email User email
   * @param password User password
   * @returns Promise with user and token
   */
  static async login(email: string, password: string): Promise<{ user: User; token: string }> {
    try {
      // Check license status before allowing login
      /* const licenseCheck = await LicenseService.checkLoginPermission();

      if (!licenseCheck.allowed) {
        throw new Error(licenseCheck.reason || 'Login blocked due to license issues');
      } */

      // Don't use hooks outside of React components
      const response = await fetch(`${getBaseUrl()}/auth.login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          json: {
            email,
            password,
          },
        }),
        credentials: 'include',
      });

      const responseData = await response.json() as any;
      // Handle the case where data is returned as a JSON string
      let data;
      // Handle superjson format (nested in json property)
      if (responseData.result?.data?.json) {
        data = responseData.result.data.json;
      } else if (responseData.result?.data && typeof responseData.result.data === 'string') {
        try {
          data = JSON.parse(responseData.result.data);
        } catch (e) {
          console.error('Error parsing response data:', e);
          throw new Error('Invalid response format from server');
        }
      } else {
        data = responseData.result?.data;
      }

      if (!data?.token || !data?.user) {
        throw new Error('Invalid response from server');
      }

      // Store in localStorage
      this.setToken(data.token);
      this.setCurrentUser(data.user);

      // Store token in multiple locations for redundancy
      if (typeof window !== 'undefined') {
        // Set as cookie that will be included in API requests
        document.cookie = `smartcity_auth_token=${data.token}; path=/; max-age=86400`;

        // Store in sessionStorage as well for added persistence
        sessionStorage.setItem(TOKEN_KEY, data.token);
      }

      return { user: data.user, token: data.token };
    } catch (error) {
      console.error('Login error:', error);
      throw error instanceof Error ? error : new Error('Authentication failed');
    }
  }

  /**
   * Register a new user
   * @param userData User registration data
   * @returns Promise with user and token
   */
  static async register(userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<{ user: User; token: string }> {
    try {
      // Don't use hooks outside of React components
      const response = await fetch(`${getBaseUrl()}/auth.register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          json: userData,
        }),
        credentials: 'include',
      });

      const responseData = await response.json() as any;
      // Handle the case where data is returned as a JSON string
      let data;
      // Handle superjson format (nested in json property)
      if (responseData.result?.data?.json) {
        data = responseData.result.data.json;
      } else if (responseData.result?.data && typeof responseData.result.data === 'string') {
        try {
          data = JSON.parse(responseData.result.data);
        } catch (e) {
          console.error('Error parsing response data:', e);
          throw new Error('Invalid response format from server');
        }
      } else {
        data = responseData.result?.data;
      }

      if (!data?.token || !data?.user) {
        throw new Error('Invalid response from server');
      }

      // Store in localStorage
      this.setToken(data.token);
      this.setCurrentUser(data.user);

      return { user: data.user, token: data.token };
    } catch (error) {
      console.error('Registration error:', error);
      throw error instanceof Error ? error : new Error('Registration failed');
    }
  }

  /**
   * Log out the current user
   */
  static logout(): void {
    if (typeof window === 'undefined') return;

    console.log('AuthService: Clearing all authentication data');

    // Clear all storage locations
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(TOKEN_KEY);

    // Clear all possible token cookies by setting them to expired
    document.cookie = `${TOKEN_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${window.location.hostname}`;
    document.cookie = `auth-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${window.location.hostname}`;
    document.cookie = `smartcity_user=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${window.location.hostname}`;
    document.cookie = `next-auth.session-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${window.location.hostname}`;
    document.cookie = `__Secure-next-auth.session-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${window.location.hostname}`;

    // Also try clearing without domain specification
    document.cookie = `${TOKEN_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
    document.cookie = `auth-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
    document.cookie = `smartcity_user=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
    document.cookie = `next-auth.session-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
    document.cookie = `__Secure-next-auth.session-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;

    // Do a console log to confirm logout
    console.log('Logged out - all auth tokens cleared');
  }

  /**
   * Check if the current token is expired
   */
  static isTokenExpired(): boolean {
    const token = this.getToken();
    return !token || isTokenExpired(token);
  }

  /**
   * Get user info from the API using tRPC
   */
  static async getUserInfoFromApi(): Promise<User | null> {
    try {
      // Don't use hooks outside of React components
      const response = await fetch(`${getBaseUrl()}/auth.me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${AuthService.getToken()}`,
        },
        credentials: 'include',
      });

      const responseData = await response.json() as any;

      // Handle the case where data is returned as a JSON string
      let data;
      // Handle superjson format (nested in json property)
      if (responseData.result?.data?.json) {
        data = responseData.result.data.json;
      } else if (responseData.result?.data && typeof responseData.result.data === 'string') {
        try {
          data = JSON.parse(responseData.result.data);
        } catch (e) {
          console.error('Error parsing response data:', e);
          return null;
        }
      } else {
        data = responseData.result?.data;
      }

      return data;
    } catch (error) {
      console.error('Failed to get user info:', error);
      return null;
    }
  }
}

/**
 * React hook for authentication state
 */
export function useAuth() {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
    loading: true,
  });

  // Initialize auth state
  const initAuth = () => {
    const token = AuthService.getToken();
    const user = AuthService.getCurrentUser();

    // Set initial state
    setState({
      isAuthenticated: !!token && !AuthService.isTokenExpired(),
      user,
      token,
      loading: false,
    });

    // If token exists but user info is missing or potentially outdated, fetch from API
    if (token && (!user || AuthService.isTokenExpired())) {
      refreshUserInfo();
    }
  };

  // Refresh user info from API
  const refreshUserInfo = async () => {
    setState((prev) => ({ ...prev, loading: true }));

    try {
      const user = await AuthService.getUserInfoFromApi();

      if (user) {
        AuthService.setCurrentUser(user);
        setState({
          isAuthenticated: true,
          user,
          token: AuthService.getToken(),
          loading: false,
        });
      } else {
        // If can't get user info, clear auth state
        AuthService.logout();
        setState({
          isAuthenticated: false,
          user: null,
          token: null,
          loading: false,
        });
      }
    } catch (error) {
      console.error('Failed to refresh user info:', error);
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  // Login handler
  const login = async (email: string, password: string) => {
    setState((prev) => ({ ...prev, loading: true }));

    try {
      const { user, token } = await AuthService.login(email, password);

      setState({
        isAuthenticated: true,
        user,
        token,
        loading: false,
      });

      return { success: true };
    } catch (error) {
      setState((prev) => ({ ...prev, loading: false }));
      console.error('Login error:', error);

      // Check if this is a license-related error
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      const isLicenseError = errorMessage.includes('license') || errorMessage.includes('License');

      return {
        success: false,
        error: errorMessage,
        isLicenseBlocked: isLicenseError,
      };
    }
  };

  // Register handler
  const register = async (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }) => {
    setState((prev) => ({ ...prev, loading: true }));

    try {
      const { user, token } = await AuthService.register(userData);

      setState({
        isAuthenticated: true,
        user,
        token,
        loading: false,
      });

      return { success: true };
    } catch (error) {
      setState((prev) => ({ ...prev, loading: false }));
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Registration failed',
      };
    }
  };

  // Logout handler
  const logout = () => {
    AuthService.logout();

    setState({
      isAuthenticated: false,
      user: null,
      token: null,
      loading: false,
    });
  };

  // Initialize auth state on mount
  useEffect(() => {
    initAuth();
  }, []);

  return {
    isAuthenticated: state.isAuthenticated,
    user: state.user,
    token: state.token,
    loading: state.loading,
    login,
    register,
    logout,
    refreshUserInfo,
  };
}
